import { useQuery } from '@tanstack/react-query';
import PQueue from 'p-queue';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { throttle } from 'underscore';

import { GeodataImage } from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../utils/variables';
import { useErrorHandling } from '../../../hooks/use-error-handling';

async function getImages(geodataId: string, photoCount: number) {
  const pQueue = new PQueue({ concurrency: 3 });
  const images: GeodataImage[] = [];
  const pageLimit = 2000;
  const chunksCount = Math.ceil(photoCount / pageLimit);

  for (let i = 0; i < chunksCount; i++) {
    void pQueue.add(async () => {
      const response = await SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().getImages({
        path: {
          geodataId,
        },
        query: {
          limit: pageLimit,
          offset: i * pageLimit,
          include: ['geodataImage.exifData'],
        },
      });
      images.push(...response.data);
    });
  }

  await pQueue.onIdle();

  return images;
}

/**
 * Returns a list of images for the geodata.
 */
export function useGetGeodataImageListQuery(geodataId: string, photoCount: number) {
  const { handleError, hasError, clearErrors, buildErrorList } = useErrorHandling();

  const { data, refetch, error, isPending, isRefetching } = useQuery({
    queryKey: ['getGeodataImages', geodataId, photoCount],
    queryFn: async () => getImages(geodataId, photoCount),
    enabled: !!geodataId,
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  // TanStack Query v5 recreates refetch on every query-state change. Pin it in a ref
  // so the throttled wrapper and its callers get a stable function identity.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const stableRefetch = useCallback(() => void refetchRef.current(), []);

  const throttledRefetch = useMemo(
    () => throttle(stableRefetch, 2000, { leading: false, trailing: true }),
    [stableRefetch],
  );

  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: () => void refetchRef.current() });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error]);

  const images = useMemo(() => data ?? [], [data]);

  return {
    images,
    refetchImages: throttledRefetch,
    buildErrorListImages: buildErrorList,
    hasError,
    isPendingGetImages: isPending || isRefetching,
  };
}
